import React from "react";
import { Link } from "react-router-dom";
import { formatINR } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Plus, Star } from "lucide-react";

export default function ProductCard({ product, index = 0, compact = false }) {
    const { addItem } = useCart();

    // Single-chip rule: best seller > discount > none. Only ONE chip ever.
    const discount = product.compare_at_price
        ? Math.round(
              ((product.compare_at_price - product.price) /
                  product.compare_at_price) *
                  100,
          )
        : 0;

    const chip = product.best_seller
        ? { label: "Best seller", solid: false }
        : discount > 0
          ? { label: `−${discount}%`, solid: true }
          : null;

    return (
        <div
            className="group"
            data-testid={`product-card-${product.slug}`}
            style={{ animationDelay: `${index * 60}ms` }}
        >
            <Link
                to={`/product/${product.slug}`}
                className="block relative overflow-hidden"
                style={{ background: "var(--vl-surface)" }}
            >
                <div className="relative" style={{ aspectRatio: "4 / 5" }}>
                    <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="vl-img absolute inset-0 w-full h-full object-cover"
                    />
                    {chip && (
                        <span
                            className="absolute top-3 left-3 overline"
                            style={
                                chip.solid
                                    ? {
                                          background: "var(--vl-rose)",
                                          color: "var(--vl-bg)",
                                          padding: "6px 10px",
                                      }
                                    : {
                                          background: "rgba(10,10,11,0.72)",
                                          backdropFilter: "blur(8px)",
                                          padding: "6px 10px",
                                          color: "var(--vl-text)",
                                          border: "1px solid var(--vl-border)",
                                      }
                            }
                            data-testid={`product-badge-${product.slug}`}
                        >
                            {chip.label}
                        </span>
                    )}
                </div>
            </Link>

            <div className="pt-5 pb-1">
                <div
                    className="overline"
                    style={{ color: "var(--vl-muted)" }}
                >
                    {product.category}
                </div>
                <div className="mt-2 flex items-start justify-between gap-3">
                    <Link
                        to={`/product/${product.slug}`}
                        className="font-serif text-lg sm:text-xl leading-tight block hover:text-[var(--vl-rose)] transition-colors"
                        style={{ color: "var(--vl-text)" }}
                        data-testid={`product-name-${product.slug}`}
                    >
                        {product.name}
                    </Link>
                    <div className="text-right shrink-0">
                        <div
                            className="text-sm"
                            style={{ color: "var(--vl-text)" }}
                            data-testid={`product-price-${product.slug}`}
                        >
                            {formatINR(product.price)}
                        </div>
                        {product.compare_at_price && (
                            <div
                                className="text-[11px] line-through"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                {formatINR(product.compare_at_price)}
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className="mt-2 flex items-center gap-1.5 text-[11px]"
                    style={{ color: "var(--vl-muted)" }}
                >
                    <Star
                        size={11}
                        fill="var(--vl-rose)"
                        color="var(--vl-rose)"
                        strokeWidth={0}
                    />
                    <span>
                        {product.rating} · {product.reviews_count} private
                        reviews
                    </span>
                </div>
            </div>

            {!compact && (
                <button
                    onClick={() => addItem(product, 1)}
                    data-testid={`product-add-${product.slug}`}
                    className="vl-btn vl-btn-ghost w-full justify-center mt-4"
                    style={{ padding: "12px 16px" }}
                >
                    <Plus size={14} strokeWidth={1.5} /> Add to bag
                </button>
            )}
        </div>
    );
}
